import { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import type { CreateTierDefinitionRequest } from '../../../../infrastructure/LoyaltyClient';

interface CreateTierModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (request: CreateTierDefinitionRequest) => Promise<void>;
  isSubmitting: boolean;
}

export const CreateTierModal = ({ show, onHide, onSubmit, isSubmitting }: CreateTierModalProps) => {
  const [tier, setTier] = useState<'Bronze' | 'Silver' | 'Gold' | 'Platinum'>('Bronze');
  const [rank, setRank] = useState('');
  const [minPoints, setMinPoints] = useState('');
  const [benefits, setBenefits] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      tier,
      rank: parseInt(rank),
      minPoints: parseInt(minPoints),
      benefits,
    });
    setTier('Bronze');
    setRank('');
    setMinPoints('');
    setBenefits('');
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTier('Bronze');
      setRank('');
      setMinPoints('');
      setBenefits('');
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop={isSubmitting ? 'static' : true}>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Create New Tier</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Tier Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select value={tier} onChange={(e) => setTier(e.target.value as any)} required disabled={isSubmitting}>
              <option value="Bronze">Bronze</option>
              <option value="Silver">Silver</option>
              <option value="Gold">Gold</option>
              <option value="Platinum">Platinum</option>
            </Form.Select>
            <Form.Text className="text-muted">Select the tier level</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Rank <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              required
              min="1"
              step="1"
              disabled={isSubmitting}
              placeholder="e.g., 1 for lowest, 4 for highest"
            />
            <Form.Text className="text-muted">Numerical rank for ordering (1 = lowest tier)</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Minimum Points <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={minPoints}
              onChange={(e) => setMinPoints(e.target.value)}
              required
              min="0"
              step="1"
              disabled={isSubmitting}
              placeholder="e.g., 1000"
            />
            <Form.Text className="text-muted">Minimum points required to reach this tier</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Benefits <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={benefits}
              onChange={(e) => setBenefits(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Describe the benefits for this tier..."
            />
            <Form.Text className="text-muted">Benefits description for tier members</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Tier'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

